import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';

// Icons
const UsersIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const UserPlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const CrownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ROLE_CONFIG = {
  owner: { label: 'Owner', color: '#f59e0b', icon: CrownIcon },
  admin: { label: 'Admin', color: '#3b82f6', icon: ShieldIcon },
  staff: { label: 'Staff', color: '#8b5cf6', icon: UserIcon },
};

const LocationStaff = ({ location, role: myRole }) => {
  const { theme } = useTheme();

  // State
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all'); // 'all' or 'admins'
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [selectedRole, setSelectedRole] = useState('staff');
  const [editingStaff, setEditingStaff] = useState(null);

  // Check permissions
  const canManageStaff = myRole === 'owner' || myRole === 'admin';
  const isOwner = myRole === 'owner';

  // Fetch staff on mount
  useEffect(() => {
    if (location?.id) {
      fetchStaff();
    }
  }, [location?.id]);

  const fetchStaff = async () => {
    try {
      const { data, error } = await supabase
        .from('location_staff')
        .select(`
          id,
          user_id,
          role,
          status,
          can_add_staff,
          invited_at,
          accepted_at,
          invited_by,
          users!location_staff_user_id_fkey (
            id,
            username,
            avatar_url,
            first_name,
            last_name
          )
        `)
        .eq('location_id', location.id)
        .order('role', { ascending: true });

      if (error) throw error;

      // Sort: owner first, then admin, then staff
      const roleOrder = { owner: 0, admin: 1, staff: 2 };
      const sorted = (data || []).sort((a, b) =>
        (roleOrder[a.role] || 3) - (roleOrder[b.role] || 3)
      );

      setStaff(sorted);
    } catch (err) {
      console.error('Error fetching staff:', err);
    } finally {
      setLoading(false);
    }
  };

  // Find user by exact username
  const findUser = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, avatar_url, first_name, last_name')
        .ilike('username', searchQuery.trim())
        .single();

      if (error || !data) {
        setSearchResults([{ notFound: true }]);
      } else {
        // Check if already staff
        const isExisting = staff.find(s => s.user_id === data.id);
        if (isExisting) {
          setSearchResults([{ ...data, alreadyStaff: true, status: isExisting.status }]);
        } else {
          setSearchResults([data]);
        }
      }
    } catch (err) {
      console.error('Error finding user:', err);
      setSearchResults([{ notFound: true }]);
    } finally {
      setSearching(false);
    }
  };

  // Invite user
  const inviteUser = async (user) => {
    setInviting(true);
    try {
      // Get current user for invited_by
      const { data: { user: currentUser } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('location_staff')
        .insert({
          location_id: location.id,
          user_id: user.id,
          role: selectedRole,
          status: 'pending',
          can_add_staff: selectedRole === 'admin',
          invited_by: currentUser?.id,
        });

      if (error) throw error;

      setShowAddModal(false);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedRole('staff');
      fetchStaff();
    } catch (err) {
      console.error('Error inviting user:', err);
      alert('Error inviting user. They may already be staff.');
    } finally {
      setInviting(false);
    }
  };

  // Update staff role
  const updateStaffRole = async (staffId, newRole) => {
    console.log('[Staff] Updating role:', staffId, 'to', newRole);
    try {
      const { data, error } = await supabase
        .from('location_staff')
        .update({
          role: newRole,
          can_add_staff: newRole === 'owner' || newRole === 'admin',
        })
        .eq('id', staffId)
        .select();

      console.log('[Staff] Update result:', data, error);

      if (error) throw error;
      setEditingStaff(null);
      await fetchStaff();
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Error updating role: ' + err.message);
    }
  };

  // Remove staff
  const removeStaff = async (staffId) => {
    if (!window.confirm('Remove this staff member?')) return;

    try {
      const { error } = await supabase
        .from('location_staff')
        .delete()
        .eq('id', staffId);

      if (error) throw error;
      fetchStaff();
    } catch (err) {
      console.error('Error removing staff:', err);
      alert('Error removing staff member.');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get initials for avatar
  const getInitials = (username) => {
    return username?.slice(0, 2).toUpperCase() || '??';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={{ ...styles.spinner, borderTopColor: '#8b5cf6' }} />
        </div>
      </div>
    );
  }

  // Calculate counts
  const ownerCount = staff.filter(s => s.role === 'owner').length;
  const adminCount = staff.filter(s => s.role === 'admin').length;
  const staffCount = staff.filter(s => s.role === 'staff').length;

  // Filter staff based on search and role filter
  const filteredStaff = staff.filter(member => {
    // Role filter
    if (filterRole === 'admins' && member.role === 'staff') return false;

    // Search filter
    if (filterSearchQuery) {
      const username = member.users?.username?.toLowerCase() || '';
      return username.includes(filterSearchQuery.toLowerCase());
    }
    return true;
  });

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ ...styles.header, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={{ ...styles.iconContainer, backgroundColor: 'rgba(34, 197, 94, 0.15)' }}>
          <UsersIcon />
        </div>
        <div style={styles.headerText}>
          <h2 style={{ ...styles.title, color: theme.text }}>Staff</h2>
          <p style={{ ...styles.subtitle, color: theme.textSecondary }}>
            {staff.length} Total
          </p>
          <div style={styles.countsRow}>
            <span style={{ ...styles.countBadge, backgroundColor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
              {staffCount} Staff
            </span>
            <span style={{ ...styles.countBadge, backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
              {adminCount} Admin{adminCount !== 1 ? 's' : ''}
            </span>
            <span style={{ ...styles.countBadge, backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
              {ownerCount} Owner
            </span>
          </div>
        </div>
        <button
          style={styles.infoButton}
          onClick={() => setShowInfoModal(true)}
        >
          <InfoIcon />
        </button>
        {canManageStaff && (
          <button
            style={styles.addButton}
            onClick={() => setShowAddModal(true)}
          >
            <UserPlusIcon />
            <span>Add</span>
          </button>
        )}
      </div>

      {/* Search & Filter Row */}
      <div style={styles.filterRow}>
        <div style={{ ...styles.filterSearchWrapper, flex: 3 }}>
          <div style={styles.searchIconWrapper}>
            <SearchIcon />
          </div>
          <input
            type="text"
            value={filterSearchQuery}
            onChange={(e) => setFilterSearchQuery(e.target.value)}
            placeholder="Search staff..."
            style={{
              ...styles.filterSearchInput,
              backgroundColor: theme.bgSheet,
              borderColor: theme.border,
              color: theme.text,
            }}
          />
        </div>
        <div style={{ ...styles.toggleWrapper, flex: 1, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
          <button
            style={{
              ...styles.toggleBtn,
              backgroundColor: filterRole === 'all' ? '#8b5cf6' : 'transparent',
              color: filterRole === 'all' ? '#fff' : theme.textSecondary,
            }}
            onClick={() => setFilterRole('all')}
          >
            All
          </button>
          <button
            style={{
              ...styles.toggleBtn,
              backgroundColor: filterRole === 'admins' ? '#3b82f6' : 'transparent',
              color: filterRole === 'admins' ? '#fff' : theme.textSecondary,
            }}
            onClick={() => setFilterRole('admins')}
          >
            Admins
          </button>
        </div>
      </div>

      {/* Staff List */}
      <div style={styles.staffList}>
        {filteredStaff.map((member) => {
          const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.staff;
          const RoleIcon = roleConfig.icon;
          const isPending = member.status === 'pending';

          return (
            <div
              key={member.id}
              style={{
                ...styles.staffCard,
                backgroundColor: theme.bgSheet,
                borderColor: theme.border,
                opacity: isPending ? 0.7 : 1,
              }}
            >
              {/* Avatar */}
              <div style={{ ...styles.avatar, backgroundColor: `${roleConfig.color}20` }}>
                {member.users?.avatar_url ? (
                  <img src={member.users.avatar_url} alt="" style={styles.avatarImg} />
                ) : (
                  <span style={{ ...styles.avatarText, color: roleConfig.color }}>
                    {getInitials(member.users?.username)}
                  </span>
                )}
              </div>

              {/* Info */}
              <div style={styles.staffInfo}>
                <div style={styles.staffNameRow}>
                  <span style={{ ...styles.staffName, color: theme.text }}>
                    {member.users?.first_name || member.users?.last_name
                      ? `${member.users?.first_name || ''} ${member.users?.last_name || ''}`.trim()
                      : member.users?.username || 'Unknown'}
                  </span>
                  {isPending && (
                    <span style={styles.pendingBadge}>
                      <ClockIcon />
                      <span>Pending</span>
                    </span>
                  )}
                </div>
                {(member.users?.first_name || member.users?.last_name) && (
                  <span style={{ ...styles.staffUsername, color: theme.textMuted }}>
                    @{member.users?.username}
                  </span>
                )}
                <div style={{ ...styles.roleBadge, backgroundColor: `${roleConfig.color}20` }}>
                  <RoleIcon />
                  <span style={{ color: roleConfig.color }}>{roleConfig.label}</span>
                </div>
                {member.accepted_at && (
                  <span style={{ ...styles.joinDate, color: theme.textMuted }}>
                    Joined {formatDate(member.accepted_at)}
                  </span>
                )}
              </div>

              {/* Actions */}
              {canManageStaff && member.role !== 'owner' && (
                <div style={styles.staffActions}>
                  {isOwner && (
                    <div style={styles.roleSelectWrapper}>
                      <select
                        value={member.role}
                        onChange={(e) => updateStaffRole(member.id, e.target.value)}
                        style={{
                          ...styles.roleSelect,
                          backgroundColor: theme.bg,
                          borderColor: theme.border,
                          color: theme.text,
                        }}
                      >
                        <option value="staff">Staff</option>
                        <option value="admin">Admin</option>
                      </select>
                      <div style={styles.selectChevron}>
                        <ChevronDownIcon />
                      </div>
                    </div>
                  )}
                  <button
                    style={styles.removeBtn}
                    onClick={() => removeStaff(member.id)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowInfoModal(false)} />
          <div style={{ ...styles.infoModalContainer, backgroundColor: theme.bgSheet }}>
            <div style={{ ...styles.modalHeader, borderColor: theme.border }}>
              <h3 style={{ ...styles.modalTitle, color: theme.text }}>Role Permissions</h3>
              <button
                style={{ ...styles.modalClose, color: theme.textSecondary }}
                onClick={() => setShowInfoModal(false)}
              >
                <XIcon />
              </button>
            </div>
            <div style={styles.infoContent}>
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoIconWrap, backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                  <CrownIcon />
                </div>
                <div style={styles.infoTextWrap}>
                  <span style={{ ...styles.infoLabel, color: '#f59e0b' }}>Owner</span>
                  <span style={{ ...styles.infoDesc, color: theme.textSecondary }}>Full control, manage all staff</span>
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoIconWrap, backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                  <ShieldIcon />
                </div>
                <div style={styles.infoTextWrap}>
                  <span style={{ ...styles.infoLabel, color: '#3b82f6' }}>Admin</span>
                  <span style={{ ...styles.infoDesc, color: theme.textSecondary }}>Add staff, edit settings</span>
                </div>
              </div>
              <div style={styles.infoItem}>
                <div style={{ ...styles.infoIconWrap, backgroundColor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                  <UserIcon />
                </div>
                <div style={styles.infoTextWrap}>
                  <span style={{ ...styles.infoLabel, color: '#8b5cf6' }}>Staff</span>
                  <span style={{ ...styles.infoDesc, color: theme.textSecondary }}>View trades & info only</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Staff Modal */}
      {showAddModal && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)} />
          <div style={{ ...styles.modal, backgroundColor: theme.bgSheet }}>
            <div style={{ ...styles.modalHeader, borderColor: theme.border }}>
              <h3 style={{ ...styles.modalTitle, color: theme.text }}>Add Staff Member</h3>
              <button
                style={{ ...styles.modalClose, color: theme.textSecondary }}
                onClick={() => setShowAddModal(false)}
              >
                <XIcon />
              </button>
            </div>

            <div style={styles.modalContent}>
              {/* Username Input with Find Button */}
              <div style={styles.findUserRow}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchResults([]);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && findUser()}
                  placeholder="Enter exact username..."
                  style={{
                    ...styles.findUserInput,
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                  autoFocus
                />
                <button
                  style={styles.findUserBtn}
                  onClick={findUser}
                  disabled={searching || searchQuery.length < 2}
                >
                  {searching ? '...' : 'Find User'}
                </button>
              </div>

              {/* Role Selector */}
              <div style={styles.roleSelector}>
                <span style={{ ...styles.roleSelectorLabel, color: theme.textSecondary }}>
                  Invite as:
                </span>
                <div style={styles.roleOptions}>
                  <button
                    style={{
                      ...styles.roleOption,
                      backgroundColor: selectedRole === 'staff' ? '#8b5cf620' : 'transparent',
                      borderColor: selectedRole === 'staff' ? '#8b5cf6' : theme.border,
                      color: selectedRole === 'staff' ? '#8b5cf6' : theme.textSecondary,
                    }}
                    onClick={() => setSelectedRole('staff')}
                  >
                    Staff
                  </button>
                  <button
                    style={{
                      ...styles.roleOption,
                      backgroundColor: selectedRole === 'admin' ? '#3b82f620' : 'transparent',
                      borderColor: selectedRole === 'admin' ? '#3b82f6' : theme.border,
                      color: selectedRole === 'admin' ? '#3b82f6' : theme.textSecondary,
                    }}
                    onClick={() => setSelectedRole('admin')}
                  >
                    Admin
                  </button>
                </div>
              </div>

              {/* Search Results */}
              {searching ? (
                <div style={styles.searchLoading}>
                  <div style={{ ...styles.spinnerSmall, borderTopColor: '#8b5cf6' }} />
                  <span style={{ color: theme.textSecondary }}>Finding user...</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div style={styles.searchResults}>
                  {searchResults.map((user, idx) => {
                    if (user.notFound) {
                      return (
                        <div key="notfound" style={{ ...styles.notFoundBox, backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
                          <span style={{ color: '#ef4444', fontWeight: 500 }}>User not found</span>
                          <span style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                            Check the username and try again
                          </span>
                        </div>
                      );
                    }

                    const displayName = user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : user.username;

                    return (
                      <div
                        key={user.id}
                        style={{ ...styles.searchResult, borderColor: theme.border, backgroundColor: theme.bg }}
                      >
                        <div style={{ ...styles.resultAvatar, backgroundColor: theme.bgSheet }}>
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" style={styles.avatarImg} />
                          ) : (
                            <span style={{ color: theme.textSecondary }}>
                              {getInitials(user.first_name || user.username)}
                            </span>
                          )}
                        </div>
                        <div style={styles.resultInfo}>
                          <span style={{ ...styles.resultName, color: theme.text }}>
                            {displayName}
                          </span>
                          <span style={{ ...styles.resultUsername, color: theme.textMuted }}>
                            @{user.username}
                          </span>
                        </div>
                        {user.alreadyStaff ? (
                          <span style={{
                            ...styles.alreadyBadge,
                            backgroundColor: user.status === 'pending' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                            color: user.status === 'pending' ? '#f59e0b' : '#22c55e',
                          }}>
                            {user.status === 'pending' ? 'Pending' : 'Already Staff'}
                          </span>
                        ) : (
                          <button
                            style={styles.inviteBtn}
                            onClick={() => inviteUser(user)}
                            disabled={inviting}
                          >
                            {inviting ? '...' : 'Invite'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={styles.searchHint}>
                  <span style={{ color: theme.textMuted }}>
                    Enter a username and tap "Find User"
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 16,
    paddingBottom: 120,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 48,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid rgba(0,0,0,0.1)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  spinnerSmall: {
    width: 20,
    height: 20,
    border: '2px solid rgba(0,0,0,0.1)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    borderRadius: 16,
    border: '1px solid',
    marginBottom: 16,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#22c55e',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    margin: '4px 0 0 0',
  },
  countsRow: {
    display: 'flex',
    gap: 8,
    marginTop: 6,
  },
  countBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: 6,
  },
  infoButton: {
    padding: 10,
    backgroundColor: 'rgba(107, 114, 128, 0.1)',
    color: '#6b7280',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px',
    backgroundColor: '#22c55e',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  filterRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
  },
  filterSearchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  filterSearchInput: {
    width: '100%',
    padding: '12px 12px 12px 40px',
    borderRadius: 10,
    border: '1px solid',
    fontSize: 14,
    outline: 'none',
  },
  toggleWrapper: {
    display: 'flex',
    borderRadius: 10,
    border: '1px solid',
    overflow: 'hidden',
  },
  toggleBtn: {
    flex: 1,
    padding: '10px 8px',
    border: 'none',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  staffList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: 20,
  },
  staffCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: 14,
    borderRadius: 12,
    border: '1px solid',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: 700,
  },
  staffInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  staffNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  staffName: {
    fontSize: 15,
    fontWeight: 600,
  },
  staffUsername: {
    fontSize: 12,
    marginTop: -2,
  },
  pendingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    backgroundColor: 'rgba(245, 158, 11, 0.15)',
    color: '#f59e0b',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
  },
  roleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    width: 'fit-content',
  },
  joinDate: {
    fontSize: 11,
    marginTop: 2,
  },
  staffActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  roleSelectWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  roleSelect: {
    appearance: 'none',
    WebkitAppearance: 'none',
    padding: '8px 32px 8px 12px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    outline: 'none',
  },
  selectChevron: {
    position: 'absolute',
    right: 10,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    color: '#6b7280',
  },
  removeBtn: {
    padding: 8,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Info Modal
  infoModalContainer: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 48px)',
    maxWidth: 340,
    borderRadius: 16,
    padding: 16,
    zIndex: 1001,
  },
  infoContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  infoIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoTextWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: 600,
  },
  infoDesc: {
    fontSize: 12,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: 'calc(90px + env(safe-area-inset-bottom))',
    left: 12,
    right: 12,
    maxHeight: '70vh',
    borderRadius: 16,
    padding: 16,
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: '1px solid',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  modalClose: {
    padding: 8,
    margin: -8,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflowY: 'auto',
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIconWrapper: {
    position: 'absolute',
    left: 14,
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '14px 14px 14px 44px',
    borderRadius: 12,
    border: '1px solid',
    fontSize: 15,
    outline: 'none',
  },
  findUserRow: {
    display: 'flex',
    gap: 10,
  },
  findUserInput: {
    flex: 1,
    padding: '14px',
    borderRadius: 12,
    border: '1px solid',
    fontSize: 15,
    outline: 'none',
  },
  findUserBtn: {
    padding: '14px 20px',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  resultInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  resultUsername: {
    fontSize: 12,
  },
  notFoundBox: {
    padding: 16,
    borderRadius: 10,
    border: '1px solid',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  alreadyBadge: {
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
  },
  roleSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  roleSelectorLabel: {
    fontSize: 14,
  },
  roleOptions: {
    display: 'flex',
    gap: 8,
  },
  roleOption: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
  },
  searchLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 24,
  },
  searchResults: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  searchResult: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 10,
    border: '1px solid',
  },
  resultAvatar: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    fontSize: 14,
    fontWeight: 600,
  },
  resultName: {
    flex: 1,
    fontSize: 15,
    fontWeight: 500,
  },
  inviteBtn: {
    padding: '8px 16px',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  noResults: {
    padding: 24,
    textAlign: 'center',
  },
  searchHint: {
    padding: 24,
    textAlign: 'center',
  },
};

export default LocationStaff;
